"use client"

import { useState, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import "../styles/DoublyLinkedList.css"

function DoublyLinkedList({ addLog }) {
  const [pages, setPages] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [inputPosition, setInputPosition] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(null)
  const [animationSpeed, setAnimationSpeed] = useState(500)
  const [currentOperation, setCurrentOperation] = useState(null)
  const [searchValue, setSearchValue] = useState("")
  const [searchResult, setSearchResult] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)

  const albumRef = useRef(null)

  // Generate a random image for album pages
  const getRandomImage = () => {
    const images = ["landscape", "portrait", "food", "animal", "architecture", "nature", "travel", "abstract"]
    return images[Math.floor(Math.random() * images.length)]
  }

  // Add a page at the beginning of the album
  const handleAddAtBeginning = () => {
    if (inputValue.trim() === "") return
    if (isAnimating) return

    setIsAnimating(true)
    setCurrentOperation("addBeginning")

    const newPage = {
      id: Date.now(),
      value: inputValue,
      image: getRandomImage(),
      isNew: true,
    }

    addLog(`Adding page "${inputValue}" at the beginning of the album`, "success")

    // First show the new page with animation
    setPages((prevPages) => [{ ...newPage, isFloating: true }, ...prevPages])

    // Then after a delay, place it at the beginning
    setTimeout(() => {
      setPages((prevPages) => {
        const updatedPages = prevPages.map((page, index) =>
          index === 0 ? { ...page, isFloating: false, isNew: false } : page,
        )
        return updatedPages
      })

      setTimeout(() => {
        setIsAnimating(false)
        setCurrentOperation(null)
        setInputValue("")
        setCurrentPage(0)
      }, animationSpeed)
    }, animationSpeed)
  }

  // Add a page at the end of the album
  const handleAddAtEnd = () => {
    if (inputValue.trim() === "") return
    if (isAnimating) return

    setIsAnimating(true)
    setCurrentOperation("addEnd")

    const newPage = {
      id: Date.now(),
      value: inputValue,
      image: getRandomImage(),
      isNew: true,
    }

    addLog(`Adding page "${inputValue}" at the end of the album`, "success")

    // Add the new page
    setPages((prevPages) => [...prevPages, { ...newPage, isFloating: true }])

    // Then place it at the end
    setTimeout(() => {
      setPages((prevPages) => {
        const updatedPages = [...prevPages]
        updatedPages[updatedPages.length - 1] = {
          ...updatedPages[updatedPages.length - 1],
          isFloating: false,
          isNew: false,
        }
        return updatedPages
      })

      setTimeout(() => {
        setIsAnimating(false)
        setCurrentOperation(null)
        setInputValue("")
        setCurrentPage(pages.length)
      }, animationSpeed)
    }, animationSpeed)
  }

  // Add a page at a specific position
  const handleAddAtPosition = () => {
    if (inputValue.trim() === "" || inputPosition.trim() === "") return
    if (isAnimating) return

    const position = Number.parseInt(inputPosition)
    if (isNaN(position) || position < 0 || position > pages.length) {
      addLog(`Invalid position: ${inputPosition}. Must be between 0 and ${pages.length}`, "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("addPosition")

    const newPage = {
      id: Date.now(),
      value: inputValue,
      image: getRandomImage(),
      isNew: true,
    }

    addLog(`Adding page "${inputValue}" at position ${position} of the album`, "success")

    // Navigate to the position
    setCurrentPage(position)

    // Insert the new page
    setTimeout(() => {
      setPages((prevPages) => {
        const updatedPages = [...prevPages]
        updatedPages.splice(position, 0, { ...newPage, isFloating: true })
        return updatedPages
      })

      // Then place it in position
      setTimeout(() => {
        setPages((prevPages) => {
          const updatedPages = [...prevPages]
          updatedPages[position] = {
            ...updatedPages[position],
            isFloating: false,
            isNew: false,
          }
          return updatedPages
        })

        setTimeout(() => {
          setIsAnimating(false)
          setCurrentOperation(null)
          setInputValue("")
          setInputPosition("")
        }, animationSpeed)
      }, animationSpeed)
    }, animationSpeed)
  }

  // Delete a page at a specific position
  const handleDelete = () => {
    if (inputPosition.trim() === "") return
    if (isAnimating) return
    if (pages.length === 0) {
      addLog("Cannot delete from an empty album", "error")
      return
    }

    const position = Number.parseInt(inputPosition)
    if (isNaN(position) || position < 0 || position >= pages.length) {
      addLog(`Invalid position: ${inputPosition}. Must be between 0 and ${pages.length - 1}`, "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("delete")

    addLog(`Deleting page at position ${position} from the album`, "info")

    // Navigate to the position
    setCurrentPage(position)

    // Mark the page as being deleted
    setTimeout(() => {
      setPages((prevPages) => {
        const updatedPages = [...prevPages]
        updatedPages[position] = { ...updatedPages[position], isDeleting: true }
        return updatedPages
      })

      // After animation, remove the page
      setTimeout(() => {
        setPages((prevPages) => {
          const updatedPages = [...prevPages]
          updatedPages.splice(position, 1)
          return updatedPages
        })

        setIsAnimating(false)
        setCurrentOperation(null)
        setInputPosition("")
        setCurrentPage(Math.min(position, pages.length - 2))
      }, animationSpeed)
    }, animationSpeed)
  }

  // Search for a page with a specific value
  const handleSearch = () => {
    if (searchValue.trim() === "") return
    if (isAnimating) return
    if (pages.length === 0) {
      addLog("Cannot search in an empty album", "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("search")
    setSearchResult(null)

    addLog(`Searching for page "${searchValue}" in the album`, "info")

    // Simulate search
    const searchPages = async () => {
      let found = false

      for (let i = 0; i < pages.length; i++) {
        setCurrentPage(i)
        setHighlightedIndex(i)
        await new Promise((resolve) => setTimeout(resolve, animationSpeed))

        if (pages[i].value.toLowerCase() === searchValue.toLowerCase()) {
          found = true
          setSearchResult({ index: i, value: searchValue, found: true })
          addLog(`Found page "${searchValue}" at position ${i}`, "success")
          break
        }
      }

      if (!found) {
        setSearchResult({ found: false, value: searchValue })
        addLog(`Page "${searchValue}" not found in the album`, "warning")
      }

      setTimeout(() => {
        setHighlightedIndex(null)
        setIsAnimating(false)
        setCurrentOperation(null)
        setSearchValue("")
      }, animationSpeed)
    }

    searchPages()
  }

  // Navigate to the previous page
  const handlePrevPage = () => {
    if (isAnimating || pages.length === 0 || currentPage <= 0) return
    setCurrentPage((prev) => prev - 1)
    addLog(`Navigated to previous page: ${currentPage - 1}`, "info")
  }

  // Navigate to the next page
  const handleNextPage = () => {
    if (isAnimating || pages.length === 0 || currentPage >= pages.length - 1) return
    setCurrentPage((prev) => prev + 1)
    addLog(`Navigated to next page: ${currentPage + 1}`, "info")
  }

  // Clear the entire album
  const handleClear = () => {
    if (pages.length === 0) return

    setPages([])
    setHighlightedIndex(null)
    setSearchResult(null)
    setCurrentPage(0)
    addLog("Cleared the entire photo album", "warning")
  }

  return (
    <div className="doubly-linked-list">
      <div className="list-controls">
        <div className="input-group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter a page title"
            disabled={isAnimating}
            className="page-input"
          />

          <div className="position-input">
            <input
              type="text"
              value={inputPosition}
              onChange={(e) => setInputPosition(e.target.value)}
              placeholder="Position (optional)"
              disabled={isAnimating}
              className="position-input"
            />
          </div>

          <div className="operation-buttons">
            <button
              onClick={handleAddAtBeginning}
              disabled={isAnimating || inputValue.trim() === ""}
              className="add-beginning-button"
            >
              Add at Beginning
            </button>

            <button
              onClick={handleAddAtEnd}
              disabled={isAnimating || inputValue.trim() === ""}
              className="add-end-button"
            >
              Add at End
            </button>

            <button
              onClick={handleAddAtPosition}
              disabled={isAnimating || inputValue.trim() === "" || inputPosition.trim() === ""}
              className="add-position-button"
            >
              Add at Position
            </button>

            <button
              onClick={handleDelete}
              disabled={isAnimating || inputPosition.trim() === "" || pages.length === 0}
              className="delete-button"
            >
              Delete at Position
            </button>
          </div>
        </div>

        <div className="search-group">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search page title"
            disabled={isAnimating}
            className="search-input"
          />

          <button
            onClick={handleSearch}
            disabled={isAnimating || searchValue.trim() === "" || pages.length === 0}
            className="search-button"
          >
            Search
          </button>

          <button onClick={handleClear} disabled={isAnimating || pages.length === 0} className="clear-button">
            Clear Album
          </button>
        </div>
      </div>

      <div className="album-visualization" ref={albumRef}>
        {pages.length === 0 ? (
          <div className="empty-album-message">The photo album is empty. Add some pages!</div>
        ) : (
          <div className="photo-album">
            <div className="album-navigation">
              <button className="nav-button prev" onClick={handlePrevPage} disabled={isAnimating || currentPage <= 0}>
                ← Previous
              </button>

              <div className="page-indicator">
                Page {currentPage + 1} of {pages.length}
              </div>

              <button
                className="nav-button next"
                onClick={handleNextPage}
                disabled={isAnimating || currentPage >= pages.length - 1}
              >
                Next →
              </button>
            </div>

            <div className="album-pages-container">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPage}
                  className={`album-page ${highlightedIndex === currentPage ? "highlighted" : ""} ${pages[currentPage]?.isNew ? "new-page" : ""}`}
                  initial={{ rotateY: -90, opacity: 0 }}
                  animate={
                    pages[currentPage]?.isDeleting
                      ? { rotateY: 90, opacity: 0, transition: { duration: 0.5 } }
                      : pages[currentPage]?.isFloating
                        ? { rotateY: -45, opacity: 0.7 }
                        : { rotateY: 0, opacity: 1 }
                  }
                  exit={{ rotateY: 90, opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="page-content">
                    <div className={`page-image ${pages[currentPage]?.image}`}>
                      {searchResult && searchResult.found && searchResult.index === currentPage && (
                        <div className="search-indicator">Found!</div>
                      )}
                    </div>
                    <div className="page-title">{pages[currentPage]?.value}</div>
                    <div className="page-number">{currentPage}</div>
                  </div>

                  <div className="page-links">
                    <div className="prev-link">{currentPage > 0 ? `← ${pages[currentPage - 1]?.value}` : "null"}</div>
                    <div className="next-link">
                      {currentPage < pages.length - 1 ? `${pages[currentPage + 1]?.value} →` : "null"}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="album-spine"></div>
          </div>
        )}

        {searchResult && searchResult.found === false && (
          <div className="search-not-found">Page "{searchResult.value}" not found in the album</div>
        )}
      </div>

      <div className="list-info">
        <div className="list-status">
          <div className="status-item">
            <span className="status-label">Length:</span>
            <span className="status-value">{pages.length}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Head:</span>
            <span className="status-value">{pages.length > 0 ? pages[0].value : "null"}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Tail:</span>
            <span className="status-value">{pages.length > 0 ? pages[pages.length - 1].value : "null"}</span>
          </div>
        </div>

        <div className="animation-speed">
          <label>Animation Speed:</label>
          <input
            type="range"
            min="200"
            max="1000"
            step="100"
            value={animationSpeed}
            onChange={(e) => setAnimationSpeed(Number(e.target.value))}
            disabled={isAnimating}
          />
          <span>{animationSpeed}ms</span>
        </div>
      </div>
    </div>
  )
}

export default DoublyLinkedList
